import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RangeTabs } from "../shared/RangeTabs";
import { DeltaPill } from "../shared/DeltaPill";
import { LoadingBlock, ErrorBlock, EmptyBlock } from "../shared/StateBlocks";
import { fmtDayShort, fmtInt } from "../format";
import {
  fetchArtists,
  fetchContent,
  fetchDescubre,
  fetchOverview,
  type AppPeriod,
  type ArtistSort,
  type Kpi,
  type Overview,
} from "./api";

/**
 * Dashboard · App y música — lo que pasa en la app «Mixcore» (y, más adelante,
 * mixcore.club): personas activas, música, Cultura, Descubre y el camino a la
 * entrada. Sólo totales. Fuente: backend#116 (resumen nocturno 04:30 Chile).
 */

const PERIODS: { value: AppPeriod; label: string }[] = [
  { value: "7d", label: "7 días" },
  { value: "28d", label: "28 días" },
  { value: "90d", label: "90 días" },
];

const KPIS: Array<{ key: string; label: string }> = [
  { key: "activePeople", label: "Personas activas" },
  { key: "activeUsers", label: "Con cuenta" },
  { key: "sessions", label: "Sesiones" },
  { key: "plays", label: "Reproducciones (30 s)" },
  { key: "listeners", label: "Oyentes" },
  { key: "minutes", label: "Minutos escuchados" },
  { key: "likes", label: "Me gusta" },
  { key: "follows", label: "Seguimientos" },
  { key: "artistViews", label: "Visitas a artistas" },
  { key: "checkoutStarts", label: "Inicios de compra" },
  { key: "purchases", label: "Compras (Mercado Pago)" },
  { key: "contentViews", label: "Lecturas de Cultura" },
];

const SORTS: { value: ArtistSort; label: string }[] = [
  { value: "plays", label: "Reproducciones" },
  { value: "listeners", label: "Oyentes" },
  { value: "followers", label: "Seguidores" },
  { value: "views", label: "Visitas" },
];

const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)} %`);

export function AppAnalyticsScreen() {
  const [period, setPeriod] = useState<AppPeriod>("28d");
  const [sort, setSort] = useState<ArtistSort>("plays");

  const overview = useQuery({ queryKey: ["app-analytics", "overview", period], queryFn: () => fetchOverview(period) });
  const artists = useQuery({ queryKey: ["app-analytics", "artists", period, sort], queryFn: () => fetchArtists(period, sort) });
  const descubre = useQuery({ queryKey: ["app-analytics", "descubre", period], queryFn: () => fetchDescubre(period) });
  const content = useQuery({ queryKey: ["app-analytics", "content", period], queryFn: () => fetchContent(period) });

  const fetching = overview.isFetching || artists.isFetching || descubre.isFetching || content.isFetching;
  const refetchAll = () => {
    overview.refetch();
    artists.refetch();
    descubre.refetch();
    content.refetch();
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Dashboard · App y música</p>
          <h1 className="nx-h1">La app y la música</h1>
          <p className="text-sm text-fg-muted mt-1">
            Personas activas, reproducciones, artistas, Descubre y Cultura. Se resume cada noche; el período termina ayer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeTabs options={PERIODS} value={period} onChange={setPeriod} />
          <button onClick={refetchAll} className="nx-btn-secondary" disabled={fetching} aria-label="Actualizar">
            <RefreshCw size={14} className={fetching ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {overview.isLoading ? (
        <LoadingBlock label="Cargando la analítica de la app…" />
      ) : overview.isError || !overview.data ? (
        <ErrorBlock onRetry={() => overview.refetch()} />
      ) : (
        <OverviewBlocks data={overview.data} />
      )}

      <section className="nx-card p-5 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="nx-h2">Artistas</h2>
            <p className="text-xs text-fg-subtle">Lo mismo que cada artista ve en Mixcore Pro, en totales.</p>
          </div>
          <RangeTabs options={SORTS} value={sort} onChange={setSort} />
        </div>
        {artists.isLoading ? (
          <LoadingBlock label="Cargando artistas…" />
        ) : artists.isError ? (
          <ErrorBlock onRetry={() => artists.refetch()} />
        ) : !artists.data?.length ? (
          <EmptyBlock label="Todavía no hay música escuchada en este período." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-2xs uppercase tracking-wide text-fg-subtle">
                  <th className="py-2 pr-3">Artista</th>
                  <th className="py-2 pr-3 text-right">Reproducciones</th>
                  <th className="py-2 pr-3 text-right">Oyentes</th>
                  <th className="py-2 pr-3 text-right">Minutos</th>
                  <th className="py-2 pr-3 text-right">Seguidores nuevos</th>
                  <th className="py-2 pr-3 text-right">Me gusta</th>
                  <th className="py-2 text-right">Visitas</th>
                </tr>
              </thead>
              <tbody>
                {artists.data.map((a) => (
                  <tr key={a.artistaId} className="border-t border-border-subtle">
                    <td className="py-2 pr-3 font-medium text-fg">{a.nombre || `Artista #${a.artistaId}`}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(a.plays)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(a.listeners)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(a.minutes)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(a.newFollowers)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtInt(a.likes)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtInt(a.profileViews)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="nx-card p-5 flex flex-col gap-3">
          <div>
            <h2 className="nx-h2">Descubre</h2>
            <p className="text-xs text-fg-subtle">
              {descubre.data ? `${fmtInt(descubre.data.starts)} mezclas pedidas. ` : ""}Qué filtros usa la gente al escuchar.
            </p>
          </div>
          {descubre.isLoading ? (
            <LoadingBlock label="Cargando Descubre…" />
          ) : descubre.isError || !descubre.data ? (
            <ErrorBlock onRetry={() => descubre.refetch()} />
          ) : !descubre.data.starts ? (
            <EmptyBlock label="Nadie pidió una mezcla en este período." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <RankList title="Géneros" rows={descubre.data.genres} />
              <RankList title="Ciudades" rows={descubre.data.cities} />
              <RankList title="Países" rows={descubre.data.countries} />
              <RankList title="Temas por artista" rows={descubre.data.tracksPerArtist.map((r) => ({ ...r, name: r.name === "all" ? "Todos" : r.name }))} />
            </div>
          )}
        </section>

        <section className="nx-card p-5 flex flex-col gap-3">
          <div>
            <h2 className="nx-h2">Cultura</h2>
            <p className="text-xs text-fg-subtle">Piezas más leídas en la app.</p>
          </div>
          {content.isLoading ? (
            <LoadingBlock label="Cargando Cultura…" />
          ) : content.isError ? (
            <ErrorBlock onRetry={() => content.refetch()} />
          ) : !content.data?.length ? (
            <EmptyBlock label="Todavía no hay lecturas en este período." />
          ) : (
            <ol className="flex flex-col gap-2">
              {content.data.slice(0, 10).map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate text-fg">{c.title}</span>
                  <span className="shrink-0 text-fg-muted tabular-nums">
                    {fmtInt(c.views)} lecturas · {fmtInt(c.readers)} personas
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}

function OverviewBlocks({ data }: { data: Overview }) {
  const k = (key: string): Kpi => data.kpis[key] || { value: 0, previous: null, change: null };
  const hasActivity = data.series.some((s) => s.activePeople || s.plays || s.sessions);
  return (
    <>
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {KPIS.map(({ key, label }) => {
          const v = k(key);
          return (
            <div key={key} className="nx-card p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <span className="nx-eyebrow">{label}</span>
                <DeltaPill deltaPct={v.change === null ? null : v.change * 100} />
              </div>
              <div className="text-3xl font-bold text-fg tracking-tight tabular-nums">{fmtInt(v.value)}</div>
              {v.previous !== null && (
                <div className="text-2xs text-fg-subtle">
                  vs <span className="text-fg-muted font-medium">{fmtInt(v.previous)}</span> período anterior
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="nx-card p-5 flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="nx-h2">Día a día</h2>
            <p className="text-xs text-fg-subtle">
              Personas activas y reproducciones · {fmtDayShort(data.period.from)} al {fmtDayShort(data.period.to)}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-fg-muted">
            <span>Sesiones: app {pct(data.sessionsBySource?.app)} · web {pct(data.sessionsBySource?.web)}</span>
            <span>
              Personas: iPhone {pct(data.peopleByPlatform?.ios)} · Android {pct(data.peopleByPlatform?.android)} · web {pct(data.peopleByPlatform?.web)}
            </span>
          </div>
        </div>
        {hasActivity ? (
          <div className="w-full h-72">
            <ResponsiveContainer>
              <AreaChart data={data.series.map((s) => ({ ...s, day: fmtDayShort(s.date) }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="peopleFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--nx-accent)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="var(--nx-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--nx-border-subtle)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={{ background: "var(--nx-bg-card)", border: "1px solid var(--nx-border)", borderRadius: 8, fontSize: 12 }}
                  formatter={(value, name) => [fmtInt(Number(value) || 0), String(name)]}
                />
                <Area type="monotone" dataKey="activePeople" name="Personas activas" stroke="var(--nx-accent)" strokeWidth={2} fill="url(#peopleFill)" />
                <Line type="monotone" dataKey="plays" name="Reproducciones" stroke="var(--nx-success)" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyBlock label="Sin actividad en este período. Los datos llegan cuando la app 2.0.0 esté en las tiendas." />
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <div className="nx-card p-4 flex items-center justify-between">
          <span className="text-sm text-fg-muted">Entradas mostradas sin señal (QR guardado)</span>
          <span className="text-xl font-semibold tabular-nums">{fmtInt(k("offlineTicketViews").value)}</span>
        </div>
        <div className="nx-card p-4 flex items-center justify-between">
          <span className="text-sm text-fg-muted">Temas que no se pudieron reproducir</span>
          <span className="text-xl font-semibold tabular-nums">{fmtInt(k("playerErrors").value)}</span>
        </div>
      </section>
    </>
  );
}

function RankList({ title, rows }: { title: string; rows: Array<{ name: string; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="flex flex-col gap-2">
      <h3 className="nx-eyebrow">{title}</h3>
      {!rows.length ? (
        <p className="text-xs text-fg-subtle">—</p>
      ) : (
        rows.slice(0, 8).map((r) => (
          <div key={r.name} className="flex flex-col gap-1">
            <div className="flex justify-between text-xs">
              <span className="truncate text-fg">{r.name}</span>
              <span className="tabular-nums text-fg-muted">{fmtInt(r.count)}</span>
            </div>
            <div className="h-1.5 rounded bg-bg-subtle overflow-hidden">
              <div className="h-full rounded bg-accent" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
